import { useSelector } from 'react-redux';
import type { CurrencyCode } from '@noths/polaris-client-localisation';

import type { TaxonomyNode } from 'src/redux/breadcrumbs/selectors';
import { selectDeepestTaxonomyNode } from 'src/redux/breadcrumbs/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { selectUser } from 'src/redux/user/selectors';

/**
 * View model for the taxonomy preview carousel.
 *
 * Like the recommended-products carousels, the products themselves are never
 * in the store: they come from a client-side plp-preview request the carousel
 * makes itself via usePlpPreview. This supplies that request's inputs (the
 * node to preview, the currency, and the current product to exclude), so the
 * carousel doesn't read the store directly.
 *
 * Returns null when the product has no taxonomy attributes. Unlike
 * recommendations, that alone is enough to know there is nothing to preview
 * or link to, so it is decided here rather than after a fetch, and PDPLayout
 * omits the section (and the fetch) entirely.
 */
export interface PlpPreviewViewModel {
  currencyCode: CurrencyCode;
  excludeProductCode: number;
  node: TaxonomyNode;
}

export const usePlpPreviewViewModel = (): PlpPreviewViewModel | null => {
  const node = useSelector(selectDeepestTaxonomyNode);
  const { currencyCode } = useSelector(selectUser);
  const { code } = useSelector(selectProduct);

  if (!node) {
    return null;
  }

  return { currencyCode, excludeProductCode: code, node };
};
